import { useEffect, useCallback } from 'react';
import {
  fetchOAuthKey,
  isAuthKeyExpired,
} from '../../shared/services/oauthService';

/**
 * Background manager for the OAuth Authorization key, mirroring
 * react-tnemis-fe/src/store/api/authKey.ts.
 *
 * - Fetches the key from the oauth_test endpoint (root-level Authorization_key)
 *   via the shared fetchOAuthKey — the same fetch used by the login and SSO flows.
 * - Refetches when the key is missing, on mount, on regaining network, and on
 *   a periodic interval.
 */
export const useAuthKey = () => {
  // The single OAuth fetch, shared with login + SSO ("first fetch two ways").
  const manualRefetch = useCallback(async () => {
    await fetchOAuthKey();
  }, []);

  // Refetch when the key is missing OR expired. Previously this only checked
  // for a missing key, so a present-but-stale key was never refreshed and
  // requests kept 401-ing — the "refetch not working" regression. isAuthKeyExpired
  // covers both the missing and past-TTL cases.
  const checkKeyValidTime = useCallback(() => {
    if (isAuthKeyExpired()) {
      manualRefetch();
    }
  }, [manualRefetch]);

  // On mount: fetch/refresh whenever the key is missing or expired.
  useEffect(() => {
    checkKeyValidTime();
  }, [checkKeyValidTime]);

  // Refetch when the browser comes back online.
  useEffect(() => {
    const handleOnline = () => checkKeyValidTime();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [checkKeyValidTime]);

  // Poll every 30 seconds and refetch as soon as the key crosses its TTL, so a
  // stale key is replaced before the server would start rejecting it.
  useEffect(() => {
    const interval = setInterval(checkKeyValidTime, 30000);
    return () => clearInterval(interval);
  }, [checkKeyValidTime]);

  return {
    manualRefetch,
    checkKeyValidTime,
  };
};

/**
 * Standard request headers, matching react-tnemis-fe's getHeaders().
 * Authorization comes from the OAuth key; Token from the login/SSO response.
 */
export const getHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json, text/plain, */*',
  Authorization: localStorage.getItem('AuthorizationKey') ?? '',
  Token: localStorage.getItem('token') ?? '',
});
